import React, { useEffect, useState, useMemo } from "react";
import "./App.css";

type Page =
  | "home"
  | "contact"
  | "legal";

function getPageFromPath(): Page {
  const path = window.location.pathname.toLowerCase();

  if (path.includes("contact")) return "contact";
  if (path.includes("legal")) return "legal";

  return "home";
}

function App() {
  const [page, setPage] = useState<Page>(getPageFromPath());
  const [isScrolled, setIsScrolled] = useState(false);
  const [formSent, setFormSent] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);

    handleScroll();
    window.addEventListener("scroll", handleScroll);

    const revealEls = document.querySelectorAll(".reveal");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: "0px 0px -40px 0px",
      }
    );

    revealEls.forEach((el) => observer.observe(el));

    return () => {
      window.removeEventListener("scroll", handleScroll);
      revealEls.forEach((el) => observer.unobserve(el));
      observer.disconnect();
    };
  }, [page]);

  useEffect(() => {
    const handlePopState = () => setPage(getPageFromPath());

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  const navigate = (targetPage: Page) => {
    const pathMap: Record<Page, string> = {
      home: "/",
      contact: "/contact-us",
      legal: "/legal",
    };

    window.history.pushState(null, "", pathMap[targetPage]);
    setPage(targetPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const pageContent = useMemo(() => {
    if (page === "contact") {
      return <ContactPage formSent={formSent} setFormSent={setFormSent} />;
    }

    if (page === "legal") {
      return <LegalPage />;
    }

    return <HomePage navigate={navigate} />;
  }, [page, formSent]);

  return (
    <>
      <Navbar navigate={navigate} isScrolled={isScrolled} />
      {pageContent}
      <Footer navigate={navigate} />
    </>
  );
}

type NavigateProps = {
  navigate: (page: Page) => void;
};

type NavbarProps = NavigateProps & {
  isScrolled: boolean;
};

function Navbar({ navigate, isScrolled }: NavbarProps) {
  const navigateToSection = (sectionId: string) => {
    // If we are already on the homepage, just scroll to the section
    if (window.location.pathname === "/") {
      document.getElementById(sectionId)?.scrollIntoView({
        behavior: "smooth",
      });

      return;
    }

    // If we are on /contact-us, go back home first
    window.history.pushState(null, "", "/");
    navigate("home");

    // Wait for HomePage to render, then scroll
    setTimeout(() => {
      document.getElementById(sectionId)?.scrollIntoView({
        behavior: "smooth",
      });
    }, 100);
  };

  return (
    <nav id="navbar" className={isScrolled ? "scrolled" : ""}>
      <a
        href="/"
        className="logo logo-image-link"
        aria-label="OmniMarkets Financial Technologies"
        onClick={(e) => {
          e.preventDefault();
          navigate("home");
        }}
      >
        <img
          src="/assets/OMFT dark background logo.svg"
          alt="OmniMarkets Financial Technologies"
          className="logo-image"
        />
      </a>

      <ul>
        <li>
          <a href="/#about" onClick={(e) => { e.preventDefault(); navigateToSection("about"); }}>
            About
          </a>
        </li>

        <li>
          <a href="/#solutions" onClick={(e) => { e.preventDefault(); navigateToSection("solutions"); }}>
            Solutions
          </a>
        </li>

        <li>
          <a href="/contact-us" className="nav-cta" onClick={(e) => { e.preventDefault(); navigate("contact"); }}>
            Contact
          </a>
        </li>
      </ul>
    </nav>
  );
}

function HomePage({ navigate }: NavigateProps) {
  return (
    <>
      <section id="hero">
        <div className="hero-bg">
          <div className="orb orb-1"></div>
          <div className="orb orb-2"></div>
          <div className="orb orb-3"></div>
          <div className="hero-line"></div>
        </div>

        <div className="hero-content">
          <div className="hero-eyebrow">
            <div className="eyebrow-line"></div>
            <span>Governance-First · Alternative Investments</span>
          </div>

          <h1>
            Where <em>Discipline</em>
            <br />
            Meets
            <br />
            Alternative Alpha
          </h1>

          <p className="hero-desc">
            OmniMarkets Financial Technologies delivers institutional-grade
            governance infrastructure for the alternative investment ecosystem —
            built for complexity, designed for control.
          </p>

          <div className="hero-ctas">
            <a href="#solutions" className="btn-primary">Explore Solutions</a>
            <a href="/contact-us" className="btn-ghost" onClick={(e) => { e.preventDefault(); navigate("contact"); }}>Request a Briefing</a>
          </div>
        </div>

        <div className="hero-stats">
          <div className="stat">
            <div className="stat-num">360<span>°</span></div>
            <div className="stat-label">Governance Coverage</div>
          </div>

          {/* <div className="stat">
            <div className="stat-num">AI<span>+</span></div>
            <div className="stat-label">Powered Intelligence</div>
          </div> */}
        </div>

        <div className="scroll-indicator">
          <div className="scroll-line"></div>
          <span>Scroll</span>
        </div>
      </section>

      <div className="marquee-band" aria-hidden="true">
        <div className="marquee-track">
          {[
            "Private Equity",
            "Hedge Funds",
            "Real Assets",
            "Venture Capital",
            "Fund Governance",
            "Regulatory Compliance",
            "Risk Frameworks",
            "LP Reporting",
            "Investment Infrastructure",
            "Private Credit",
            "Private Equity",
            "Hedge Funds",
            "Real Assets",
            "Venture Capital",
            "Fund Governance",
            "Regulatory Compliance",
            "Risk Frameworks",
            "LP Reporting",
            "Investment Infrastructure",
            "Private Credit",
          ].map((item, index) => (

            <span className="marquee-item" key={`${item}-${index}`}>
              {item} <span className="marquee-dot"></span>
            </span>
          ))}
        </div>
      </div>

      <section id="about">
        <div className="section-inner">
          <div className="about-grid">
            <div className="about-visual reveal">
              <div className="about-frame">
                <svg
                  viewBox="0 0 460 510"
                  preserveAspectRatio="xMidYMid slice"
                  xmlns="http\://www.w3.org/2000/svg"
                >
                  <defs>
                    <radialGradient id="bg-grad" cx="50%" cy="40%" r="60%">
                      <stop offset="0%" stopColor="#1A2A3A" />
                      <stop offset="100%" stopColor="#080A0F" />
                    </radialGradient>
                  </defs>

                  <rect width="460" height="510" fill="url(#bg-grad)" />

                  <g stroke="rgba(184,151,90,0.06)" strokeWidth="1">
                    <line x1="0" y1="85" x2="460" y2="85" />
                    <line x1="0" y1="170" x2="460" y2="170" />
                    <line x1="0" y1="255" x2="460" y2="255" />
                    <line x1="0" y1="340" x2="460" y2="340" />
                    <line x1="0" y1="425" x2="460" y2="425" />
                    <line x1="92" y1="0" x2="92" y2="510" />
                    <line x1="184" y1="0" x2="184" y2="510" />
                    <line x1="276" y1="0" x2="276" y2="510" />
                    <line x1="368" y1="0" x2="368" y2="510" />
                  </g>

                  <g fill="none" strokeWidth="1" transform="translate(230,255)">
                    <circle r="180" stroke="rgba(184,151,90,0.06)" />
                    <circle r="140" stroke="rgba(184,151,90,0.09)" />
                    <circle r="100" stroke="rgba(184,151,90,0.12)" />
                    <circle r="60" stroke="rgba(184,151,90,0.18)" />
                    <circle r="24" stroke="rgba(184,151,90,0.3)" />
                    <circle r="8" fill="rgba(184,151,90,0.5)" stroke="none" />
                  </g>

                  <g stroke="rgba(184,151,90,0.1)" strokeWidth="1" transform="translate(230,255)">
                    <line x1="0" y1="-180" x2="0" y2="-24" />
                    <line x1="0" y1="24" x2="0" y2="180" />
                    <line x1="-180" y1="0" x2="-24" y2="0" />
                    <line x1="24" y1="0" x2="180" y2="0" />
                    <line x1="-127" y1="-127" x2="-17" y2="-17" />
                    <line x1="17" y1="17" x2="127" y2="127" />
                    <line x1="127" y1="-127" x2="17" y2="-17" />
                    <line x1="-17" y1="17" x2="-127" y2="127" />
                  </g>

                  <g stroke="rgba(184,151,90,0.4)" strokeWidth="1.5" fill="none">
                    <polyline points="20,20 20,55 55,55" />
                    <polyline points="440,20 440,55 405,55" />
                    <polyline points="20,490 20,455 55,455" />
                    <polyline points="440,490 440,455 405,455" />
                  </g>

                  <g>
                    <circle cx="100" cy="100" r="4" fill="rgba(184,151,90,0.5)" />
                    <circle cx="360" cy="140" r="3" fill="rgba(58,106,155,0.6)" />
                    <circle cx="80" cy="380" r="5" fill="rgba(184,151,90,0.3)" />
                    <circle cx="380" cy="400" r="4" fill="rgba(184,151,90,0.5)" />
                    <circle cx="200" cy="60" r="3" fill="rgba(58,106,155,0.5)" />
                    <circle cx="350" cy="460" r="3" fill="rgba(184,151,90,0.4)" />
                  </g>

                  <g stroke="rgba(184,151,90,0.12)" strokeWidth="1">
                    <line x1="230" y1="255" x2="100" y2="100" />
                    <line x1="230" y1="255" x2="360" y2="140" />
                    <line x1="230" y1="255" x2="80" y2="380" />
                    <line x1="230" y1="255" x2="380" y2="400" />
                    <line x1="230" y1="255" x2="200" y2="60" />
                    <line x1="230" y1="255" x2="350" y2="460" />
                  </g>

                  <g
                    fontFamily="'DM Sans', sans-serif"
                    fill="rgba(184,151,90,0.5)"
                    fontSize="9"
                    letterSpacing="1"
                  >
                    <text x="20" y="486">OMNIMARKETS</text>
                    <text x="20" y="498">GOVERNANCE FRAMEWORK</text>
                  </g>

                  <rect x="0" y="50" width="4" height="40" fill="rgba(184,151,90,0.6)" />
                </svg>
              </div>

              <div className="about-badge">
                <div className="about-badge-num">360°</div>
                <div className="about-badge-label">Coverage</div>
              </div>
            </div>

            <div className="about-text">
              <div className="section-tag reveal">
                <div className="tag-line"></div>
                <span>About OM-FT</span>
              </div>

              <h2 className="reveal reveal-delay-1">
                Governance is
                <br />
                Not an
                <br />
                <em>Afterthought</em>
              </h2>

              <p className="reveal reveal-delay-2">
                OmniMarkets Financial Technologies was founded on a singular
                conviction: that the alternative investment industry deserves
                infrastructure where oversight, transparency, and control are
                designed in from the start — not bolted on.
              </p>

              <p className="reveal reveal-delay-2">
                We combine deep domain expertise in alternatives with advanced
                technology to deliver governance solutions that operate at
                institutional scale without sacrificing agility.
              </p>

              <div className="about-pillars">
                {[
                  ["⬡", "Transparency", "Understand every asset, valuation, and portfolio change."],
                  ["◈", "Control", "Take ownership of your data, assumptions, and valuation processes."],
                  ["◉", "Compliance", "Simplify reporting, reviews, and audit preparation."],
                  ["⬟", "Intelligence", "Identify risks, test scenarios, and make informed decisions."],
                ].map(([icon, title, desc], index) => (
                  <div className={`pillar reveal reveal-delay-${index + 1}`} key={title}>
                    <div className="pillar-icon">{icon}</div>
                    <div className="pillar-title">{title}</div>
                    <div className="pillar-desc">{desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="solutions">
        <div className="section-inner">
          <div className="solutions-header">
            <div>
              <div className="section-tag reveal">
                <div className="tag-line"></div>
                <span>Solutions</span>
              </div>

              <h2 className="reveal reveal-delay-1">
                Built for the
                <br />
                <em>Complexity</em>
                <br />
                of Alternatives
              </h2>
            </div>

            <p className="solutions-intro reveal reveal-delay-2">
              Every solution in the OmniMarkets ecosystem is architected around
              a governance-first principle — ensuring that as your strategies
              grow in sophistication, your oversight capabilities grow with them.
            </p>
          </div>

          <div className="solutions-grid">
            {[
              [
                "01",
                "LeLo\nPrivate Credit Valuation, Risk & Governance.",
                "LeLo is an integrated, Excel-native platform for private credit and leveraged loan portfolios, combining advanced valuation, risk management, portfolio monitoring, governance, and automated reporting. Its secure, collaborative environment provides transparent, traceable, and reproducible analytics, helping investment managers streamline operations, strengthen oversight, and meet investor and regulatory reporting requirements.",
              ],
              [
                "02",
                "CLeLo\nCLO Valuation, Risk & Governance.",
                "An integrated platform for valuing, monitoring, and managing CLOs and their underlying leveraged loan and private credit portfolios.\nBuilt on LeLo, CLeLo extends loan-level analytics to CLO structures, enabling comprehensive collateral analysis, tranche-level risk assessment, and transparent investment oversight.",
              ]
            ].map(([num, title, desc], index) => (
              <div className={`solution-card reveal reveal-delay-${index % 2}`} key={num}>
                <div className="solution-num">{num}</div>

                <div className="solution-icon-wrap">
                  <svg viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 6v6l4 2" />
                  </svg>
                </div>

                <div className="solution-title">{title}</div>
                <div className={`solution-desc${num === "02" ? " solution-desc-with-launch" : ""}`}>{desc}</div>
                {num === "02" && (
                  <p className="solution-launch"><em>Target launch: Q4 2026</em></p>
                )}
                
                <a href="/contact-us" className="solution-link" onClick={(e) => { e.preventDefault(); navigate("contact"); }}>
                  Learn more
                  <svg viewBox="0 0 24 24">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* <section id="cta-band">
        <div className="section-inner">
          <div className="cta-inner">
            <div className="section-tag reveal section-tag-center">
              <div className="tag-line"></div>
              <span>Get Started</span>
              <div className="tag-line"></div>
            </div>

            <h2 className="reveal reveal-delay-1">
              Ready to Build
              <br />
              <em>Governance</em> Into
              <br />
              Your Foundation?
            </h2>

            <p className="reveal reveal-delay-2">
              Join forward-thinking alternative investment managers who treat
              governance as a competitive advantage, not a compliance cost.
            </p>

            <div className="cta-actions reveal reveal-delay-3">
              <a href="/contact-us" className="btn-primary" onClick={(e) => { e.preventDefault(); navigate("contact"); }}>Request a Briefing</a>
              <a href="#solutions" className="btn-ghost">View Solutions</a>
            </div>
          </div>
        </div>
      </section> */}
    </>
  );
}

type ContactProps = {
  formSent: boolean;
  setFormSent: React.Dispatch<React.SetStateAction<boolean>>;
};

function ContactPage({ formSent, setFormSent }: ContactProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    position: "",
    message: "",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault();

    try {
      setIsSubmitting(true);

      const response = await fetch(
        "https://omft-contact-ckdjcwhtgzd5fcfq.eastus-01.azurewebsites.net/api/Contact/OMFT",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to send contact form.");
      }

      setFormSent(true);

      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        company: "",
        position: "",
        message: "",
      });

      setTimeout(() => { setFormSent(false); }, 3000);
    } catch (error) {
      console.error("Contact form error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSubmitted = formSent;
  const submitText = isSubmitted
    ? "Sent ✓"
    : isSubmitting
      ? "Sending..."
      : "Send";

  return (
    <>
      <section id="contact">
        <div className="section-inner">
          <div className="contact-grid">
            <div className="contact-info">
              <div className="section-tag reveal">
                <div className="tag-line"></div>
                <span>Contact</span>
              </div>

              <h2 className="reveal reveal-delay-1">
                Let's Start
                <br />
                <em>a Conversation</em>
              </h2>

              <p className="reveal reveal-delay-2">
                Whether you are interested in our solutions, would like to schedule a demonstration, or want to discuss your organization's specific requirements, we would welcome the opportunity to connect.
              </p>

              <p className="reveal reveal-delay-2">
                Tell us a little about what you are looking for, and our team will be in touch.
              </p>

              <div className="contact-details reveal reveal-delay-3">
                {/* <div className="contact-item">
                  <div className="contact-item-icon">✉</div>
                  <div className="contact-item-text">
                    <label>Email</label>
                    <span>inquiries@omnimarkets.com</span>
                  </div>
                </div> */}

                {/* <div className="contact-item">
                  <div className="contact-item-icon">⌖</div>
                  <div className="contact-item-text">
                    <label>Headquarters</label>
                    <span>Greenwich, CT</span>
                  </div>
                </div> */}

                {/* <div className="contact-item">
                  <div className="contact-item-icon">☎</div>
                  <div className="contact-item-text">
                    <label>Schedule</label>
                    <span>Available for calls Mon–Fri, 8am–6pm ET</span>
                  </div>
                </div> */}
              </div>
            </div>

            <form className="contact-form reveal reveal-delay-2" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input type="text" name="firstName" placeholder="First Name" value={formData.firstName} onChange={handleChange} required />
                </div>

                <div className="form-group">
                  <label>Last Name *</label>
                  <input type="text" name="lastName" placeholder="Last Name" value={formData.lastName} onChange={handleChange} required />
                </div>
              </div>

              <div className="form-group">
                <label>Email Address *</label>
                <input type="email" name="email" placeholder="Company Email" value={formData.email} onChange={handleChange} required />
              </div>

              <div className="form-group">
                <label>Company *</label>
                <input type="text" name="company" placeholder="Company" value={formData.company} onChange={handleChange} required />
              </div>

              <div className="form-group">
                <label>Position *</label>
                <input type="text" name="position" placeholder="Position" value={formData.position} onChange={handleChange} required />
              </div>

              {/* <div className="form-group">
                <label>Area of Interest</label>
                <select name="position" value={formData.position} onChange={handleSelectChange}>
                  <option value="" disabled>Select a solution area</option>
                  <option>Fund Governance Platform</option>
                  <option>Risk & Compliance Intelligence</option>
                  <option>LP Reporting & Transparency</option>
                  <option>Data Infrastructure & APIs</option>
                  <option>Advisory & Implementation</option>
                  <option>Regulatory Reporting Suite</option>
                  <option>General Inquiry</option>
                </select>
              </div> */}

              <div className="form-group">
                <label>Message *</label>
                <textarea name="message" placeholder="Tell us about your governance challenges and what you're looking to achieve..." value={formData.message} onChange={handleChange} required />
              </div>

              <button
                type="submit"
                className={`btn-primary form-submit${isSubmitted ? " submitted" : ""}`}
                disabled={isSubmitting || isSubmitted}
              >
                {submitText}
              </button>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}

function LegalPage() {
  return (
    <main className="legal-page">
      <section className="legal-hero">
        <div className="legal-hero-inner reveal">
          <div className="section-tag">
            <div className="tag-line"></div>
            <span>Legal</span>
          </div>

          <h1>Legal Information</h1>

          <p>
            This page contains important legal information for OmniMarkets Financial
            Technologies services and applications, including LeLo and CLeLo,
            covering our privacy practices, terms of use, and general disclosures.
          </p>

          <div className="legal-updated">Last updated: October 2026</div>
        </div>
      </section>

      <section className="legal-content-section">
        <div className="legal-content-card reveal reveal-delay-1">
          <article id="privacy-policy" className="legal-section-block">
            <h2>Privacy Policy</h2>

            <p>
              This Privacy Policy explains how OmniMarkets Financial Technologies
              collects, uses, shares, and protects personal information when you
              use our services and applications, including LeLo and CLeLo.
            </p>

            <h3>Information We Collect</h3>

            <p>
              We may collect information such as your name, email address,
              organization, role, account identifiers, business records, and any
              information you provide when contacting us or using our services.
            </p>

            <p>
              We may also collect technical and usage information, such as IP
              address, browser type, device information, system logs, and service
              interaction data, where permitted by applicable law.
            </p>

            <h3>How We Use Information</h3>

            <p>
              We use personal information to provide and support our services,
              respond to inquiries, communicate with clients and prospective
              clients, maintain security, meet legal and regulatory obligations,
              and improve service quality.
            </p>

            <p>
              We do not sell personal information. We may share information with
              service providers or professional advisors where necessary to operate
              our business, deliver services, respond to inquiries, or comply with
              applicable obligations.
            </p>

            <h3>Data Rights and Contact</h3>

            <p>
              Depending on your jurisdiction, you may have rights to request
              access, correction, deletion, restriction, or portability of your
              personal information, or to object to certain processing activities.
            </p>

            <p>
              To submit a privacy request or ask questions about this policy,
              please contact OmniMarkets Financial Technologies through our
              official contact channels.
            </p>
          </article>

          <article id="terms-of-service" className="legal-section-block">
            <h2>Terms of Service</h2>

            <p>
              These Terms of Service govern your access to and use of the OmniMarkets Financial Technologies website.
            </p>

            <h3>Website Use</h3>

            <p>
              This website is provided for general informational purposes. By
              accessing or using this website, you agree to use it only for lawful
              purposes and in a manner that does not interfere with the operation,
              security, or accessibility of the website.
            </p>

            <h3>No Professional Advice</h3>

            <p>
              The content on this website does not constitute investment, legal,
              tax, accounting, regulatory, or other professional advice. You should
              not rely on website content as a substitute for advice from qualified
              professional advisors.
            </p>

            <h3>Intellectual Property</h3>

            <p>
              All content, branding, graphics, text, designs, and other materials
              on this website are owned by or licensed to OmniMarkets Financial Technologies,
              unless otherwise indicated.
            </p>
          </article>

          <article id="disclosures" className="legal-section-block">
            <h2>Disclosures</h2>

            <p>
              The information on this website is provided for informational
              purposes only and is subject to the disclosures below.
            </p>

            <h3>Informational Purposes Only</h3>

            <p>
              Nothing on this website should be interpreted as a recommendation,
              solicitation, offer, or invitation to buy or sell any financial
              product, security, or investment strategy.
            </p>

            <h3>No Investment or Regulatory Advice</h3>

            <p>
              OmniMarkets Financial Technologies does not provide investment, legal, tax,
              accounting, or regulatory advice through this website.
            </p>

            <p>
              Any discussion of financial markets, derivatives, risk management,
              regulatory workflows, or technology solutions is general in nature
              and should not be relied upon as advice for any specific situation.
            </p>

            <h3>No Warranty</h3>

            <p>
              While we aim to provide accurate and current information, OmniMarkets Financial Technologies makes no representation or warranty that the website
              content is complete, accurate, or free from errors.
            </p>
          </article>
        </div>
      </section>
    </main>
  );
}

function Footer({ navigate }: NavigateProps) {
  return (
    <footer>

      <div className="footer-inner">
        <div className="footer-brand">

          <a href="/" className="footer-logo-link" aria-label="OmniMarkets Financial Technologies" onClick={(e) => { e.preventDefault(); navigate("home"); }}>

            <img
              src="/assets/OMFT dark background logo.svg"
              alt="OmniMarkets Financial Technologies"
              className="footer-logo-image"
            />
          </a>

          <p>
            Governance-first solutions for the alternative investment
            ecosystem. Building the infrastructure that institutional capital
            deserves.
          </p>
        </div>

        <div className="footer-col">
          <h4>Solutions</h4>
          <ul>
            <li><a href="#solutions">LeLo</a></li>
            <li><a href="#solutions">CLeLo</a></li>
          </ul>
        </div>

        <div className="footer-col">
          <h4>Company</h4>
          <ul>
            <li><a href="/" onClick={(e) => { e.preventDefault(); navigate("home"); }}>About</a></li>
          </ul>
        </div>

        <div className="footer-col">
          <h4>Legal</h4>
          <ul>
            <li>
              <a
                href="/legal#privacy-policy"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("legal");
                  setTimeout(() => {
                    document
                      .getElementById("privacy-policy")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }, 100);
                }}
              >
                Privacy Policy
              </a>
            </li>

            <li>
              <a
                href="/legal#terms-of-service"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("legal");
                  setTimeout(() => {
                    document
                      .getElementById("terms-of-service")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }, 100);
                }}
              >
                Terms of Service
              </a>
            </li>

            <li>
              <a
                href="/legal#disclosures"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("legal");
                  setTimeout(() => {
                    document
                      .getElementById("disclosures")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }, 100);
                }}
              >
                Disclosures
              </a>
            </li>

            {/* <li><a href="/legal#cookie-policy">Cookie Policy</a></li> */}
          </ul>
        </div>
      </div>

      <div className="footer-bottom">
        <span>© 2026 OmniMarkets Financial Technologies. All rights reserved.</span>
        <span className="footer-motto">GOVERNANCE · TRANSPARENCY · CONTROL</span>
      </div>
    </footer>
  );
}

export default App;